// Central Hub Integration for XYZ-HQ
// Configuration and utilities for connecting to the Central Hub

export const HUB_CONFIG = {
  // Hub URLs
  hubUrl: import.meta.env.VITE_HUB_URL || 'https://hub.xyzstrategy.co',
  hubApiUrl: import.meta.env.VITE_HUB_API_URL || 'https://hub.xyzstrategy.co/api',

  // This platform's identifier
  platformId: 'xyzhq' as const,

  // Callback URL for this platform
  callbackUrl: `${window.location.origin}/auth/hub-callback`,
};

export type PlatformId = 'realnumbers' | 'adcanvas' | 'xyzhq';
export type UserRole = 'owner' | 'admin' | 'manager' | 'contributor' | 'viewer';

// Hub JWT claims structure
export interface HubJWTClaims {
  sub: string;
  email: string;
  hub_user: {
    id: string;
    email: string;
    name: string;
    avatar_url?: string;
  };
  platforms: {
    [key in PlatformId]?: {
      enabled: boolean;
      organization_id: string;
      organization_name: string;
      role: UserRole;
    };
  };
  session_id: string;
  exp: number;
  iat: number;
}

// Token storage keys
const TOKEN_KEY = 'hub_access_token';
const REFRESH_TOKEN_KEY = 'hub_refresh_token';

/**
 * Redirect to the hub login page
 */
export function redirectToHubLogin(): void {
  const params = new URLSearchParams({
    platform: HUB_CONFIG.platformId,
    redirect: HUB_CONFIG.callbackUrl,
  });

  window.location.href = `${HUB_CONFIG.hubUrl}/login?${params.toString()}`;
}

/**
 * Exchange an auth code for tokens
 */
export async function exchangeCodeForTokens(code: string): Promise<{
  access_token: string;
  refresh_token: string;
  expires_in: number;
} | null> {
  try {
    const response = await fetch(`${HUB_CONFIG.hubApiUrl}/auth-token/exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        redirect_uri: HUB_CONFIG.callbackUrl,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Token exchange failed:', error);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Token exchange error:', error);
    return null;
  }
}

/**
 * Refresh the access token
 */
export async function refreshAccessToken(): Promise<{
  access_token: string;
  refresh_token: string;
  expires_in: number;
} | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${HUB_CONFIG.hubApiUrl}/auth-token/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      clearTokens();
      return null;
    }

    const tokens = await response.json();
    storeTokens(tokens.access_token, tokens.refresh_token);
    return tokens;
  } catch (error) {
    console.error('Token refresh error:', error);
    return null;
  }
}

/**
 * Revoke tokens (logout)
 */
export async function revokeTokens(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await fetch(`${HUB_CONFIG.hubApiUrl}/auth-token/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });
    } catch (error) {
      console.error('Token revocation error:', error);
    }
  }
  clearTokens();
}

/**
 * Store tokens in localStorage
 */
export function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Get the current access token
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Get the current refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Clear all stored tokens
 */
export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Check if the user has a valid token stored
 */
export function hasValidToken(): boolean {
  const token = getAccessToken();
  if (!token) return false;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp > (Date.now() / 1000) + 60;
  } catch {
    return false;
  }
}

/**
 * Decode the JWT token to get claims
 */
export function decodeToken(): HubJWTClaims | null {
  const token = getAccessToken();
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

/**
 * Get the user's access for this platform from the token
 */
export function getPlatformAccess(): {
  enabled: boolean;
  organization_id: string;
  organization_name: string;
  role: UserRole;
} | null {
  const claims = decodeToken();
  if (!claims) return null;

  const platformAccess = claims.platforms[HUB_CONFIG.platformId];
  if (!platformAccess || !platformAccess.enabled) return null;

  return platformAccess;
}

/**
 * Check if this is a hub callback URL
 */
export function isHubCallback(): boolean {
  return window.location.pathname === '/auth/hub-callback';
}

/**
 * Get the auth code from the URL
 */
export function getAuthCodeFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('code');
}
